import { useState } from "react";
import { CompanyData, Employee, EmployeeRole, EMPLOYEE_ROLES } from "@/contexts/GameContext";
import { useGame } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  company: CompanyData;
  isOwner: boolean;
}

const MOCK_CANDIDATES: Omit<Employee, "id" | "hiredAt">[] = [
  { name: "山田 健太", role: "engineer", salary: 1800, performance: 82, department: "技術", skills: ["React", "Node.js", "AWS"] },
  { name: "佐々木 由美", role: "designer", salary: 1500, performance: 88, department: "クリエイティブ", skills: ["Figma", "UI/UX", "Illustrator"] },
  { name: "中村 大輔", role: "sales", salary: 1200, performance: 75, department: "営業", skills: ["営業", "CRM", "交渉"] },
  { name: "小林 真理", role: "hr", salary: 1300, performance: 79, department: "人事", skills: ["採用", "労務管理"] },
  { name: "加藤 誠", role: "accountant", salary: 1400, performance: 85, department: "財務", skills: ["会計", "税務", "Excel"] },
  { name: "渡辺 拓也", role: "manager", salary: 2000, performance: 90, department: "管理", skills: ["プロジェクト管理", "リーダーシップ"] },
  { name: "伊藤 さくら", role: "worker", salary: 800, performance: 70, department: "製造", skills: ["製造", "品質管理"] },
  { name: "松本 浩二", role: "engineer", salary: 1900, performance: 91, department: "技術", skills: ["Python", "ML", "データ分析"] },
];

export default function EmployeePanel({ company, isOwner }: Props) {
  const { hireEmployee, fireEmployee } = useGame();
  const { isDark } = useTheme();
  const [tab, setTab] = useState<"current" | "hire">("current");
  const [filterRole, setFilterRole] = useState<EmployeeRole | "all">("all");
  const [msg, setMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [confirmFire, setConfirmFire] = useState<string | null>(null);

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const rowHover = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";
  const divider = isDark ? "divide-white/5" : "divide-gray-100";
  const tabActive = "border-amber-400 text-amber-400";
  const tabInactive = isDark ? "border-transparent text-gray-400 hover:text-white" : "border-transparent text-gray-500 hover:text-gray-900";

  const filteredEmployees = filterRole === "all"
    ? company.employees
    : company.employees.filter((e) => e.role === filterRole);

  const totalSalary = company.employees.reduce((sum, e) => sum + e.salary, 0);

  const handleHire = (candidate: Omit<Employee, "id" | "hiredAt">) => {
    const ok = hireEmployee(company.id, candidate);
    if (ok) {
      setMsg({ text: `${candidate.name}を採用しました！月給: ${candidate.salary.toLocaleString()} coins`, type: "success" });
    } else {
      setMsg({ text: "採用できませんでした（定員超過の可能性）", type: "error" });
    }
    setTimeout(() => setMsg(null), 3000);
  };

  const handleFire = (employeeId: string) => {
    const ok = fireEmployee(company.id, employeeId);
    if (ok) {
      setMsg({ text: "退職処理が完了しました", type: "success" });
    } else {
      setMsg({ text: "退職処理に失敗しました", type: "error" });
    }
    setConfirmFire(null);
    setTimeout(() => setMsg(null), 3000);
  };

  const getPerformanceColor = (p: number) => {
    if (p >= 85) return "text-emerald-400";
    if (p >= 70) return "text-amber-400";
    return "text-red-400";
  };

  return (
    <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
        <div className="flex items-center justify-between mb-1">
          <h2 className={`text-base font-bold ${textPrimary}`}>従業員管理</h2>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${textSecondary} whitespace-nowrap`}>
              月次給与合計: <span className="text-red-400 font-bold">{totalSalary.toLocaleString()} coins</span>
            </span>
            <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${isDark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
              {company.employees.length} / {company.maxEmployees}名
            </span>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex gap-0 mt-3 border-b border-transparent">
          {[
            { id: "current" as const, label: "在籍中", icon: "ri-team-line" },
            { id: "hire" as const, label: "採用候補", icon: "ri-user-add-line" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${tab === t.id ? tabActive : tabInactive}`}
            >
              <i className={`${t.icon} text-sm`}></i>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Message */}
      {msg && (
        <div className={`mx-6 mt-4 rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 ${msg.type === "success" ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20" : "bg-red-400/10 text-red-400 border border-red-400/20"}`}>
          <i className={msg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
          {msg.text}
        </div>
      )}

      {/* Current Employees */}
      {tab === "current" && (
        <div>
          {/* Role Filter */}
          <div className="px-6 py-3 flex gap-2 overflow-x-auto">
            {[{ id: "all" as const, label: "全員" }, ...Object.entries(EMPLOYEE_ROLES).map(([k, v]) => ({ id: k as EmployeeRole, label: v.label }))].map((r) => (
              <button
                key={r.id}
                onClick={() => setFilterRole(r.id)}
                className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer font-medium ${filterRole === r.id ? "bg-amber-400 text-black" : isDark ? "bg-white/5 text-gray-400 hover:bg-white/10" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {filteredEmployees.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-amber-400/10">
                <i className="ri-team-line text-2xl text-amber-400"></i>
              </div>
              <p className={`text-sm ${textSecondary}`}>従業員がいません</p>
              {isOwner && (
                <button onClick={() => setTab("hire")} className="mt-3 text-xs text-amber-400 hover:underline cursor-pointer">
                  採用候補を見る →
                </button>
              )}
            </div>
          ) : (
            <div className={`divide-y ${divider}`}>
              {filteredEmployees.map((emp) => (
                <div key={emp.id} className={`flex items-center gap-4 px-6 py-4 transition-colors ${rowHover}`}>
                  {/* Avatar */}
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 font-bold text-sm ${isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-700"}`}>
                    {emp.name.charAt(0)}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm font-semibold ${textPrimary}`}>{emp.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                        {EMPLOYEE_ROLES[emp.role].label}
                      </span>
                      <span className={`text-xs ${textSecondary}`}>{emp.department}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {emp.skills.slice(0, 3).map((s) => (
                        <span key={s} className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${isDark ? "bg-cyan-400/10 text-cyan-400" : "bg-cyan-50 text-cyan-600"}`}>{s}</span>
                      ))}
                    </div>
                  </div>
                  {/* Stats */}
                  <div className="text-right flex-shrink-0 space-y-1">
                    <p className={`text-sm font-bold whitespace-nowrap ${textPrimary}`}>{emp.salary.toLocaleString()} coins/月</p>
                    <p className={`text-xs whitespace-nowrap ${getPerformanceColor(emp.performance)}`}>
                      パフォーマンス: {emp.performance}
                    </p>
                    <p className={`text-xs ${textSecondary} whitespace-nowrap`}>
                      入社: {new Date(emp.hiredAt).toLocaleDateString("ja-JP", { month: "short", day: "numeric" })}
                    </p>
                  </div>
                  {/* Fire Button */}
                  {isOwner && (
                    <div className="flex-shrink-0">
                      {confirmFire === emp.id ? (
                        <div className="flex gap-2">
                          <button onClick={() => handleFire(emp.id)} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 cursor-pointer whitespace-nowrap font-medium">
                            確認
                          </button>
                          <button onClick={() => setConfirmFire(null)} className={`text-xs px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400 hover:bg-white/10" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                            キャンセル
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmFire(emp.id)}
                          className={`text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400" : "bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500"}`}
                        >
                          <i className="ri-user-unfollow-line mr-1"></i>退職
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hire Candidates */}
      {tab === "hire" && (
        <div>
          <div className={`px-6 py-3 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
            <p className={`text-xs ${textSecondary}`}>
              採用可能枠: <span className={`font-bold ${textPrimary}`}>{company.maxEmployees - company.employees.length}名</span>
            </p>
          </div>
          <div className={`divide-y ${divider}`}>
            {MOCK_CANDIDATES.map((candidate, i) => {
              const alreadyHired = company.employees.some((e) => e.name === candidate.name);
              return (
                <div key={i} className={`flex items-center gap-4 px-6 py-4 transition-colors ${rowHover}`}>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 font-bold text-sm ${isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-700"}`}>
                    {candidate.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm font-semibold ${textPrimary}`}>{candidate.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                        {EMPLOYEE_ROLES[candidate.role].label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {candidate.skills.map((s) => (
                        <span key={s} className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${isDark ? "bg-cyan-400/10 text-cyan-400" : "bg-cyan-50 text-cyan-600"}`}>{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 space-y-1">
                    <p className={`text-sm font-bold whitespace-nowrap ${textPrimary}`}>{candidate.salary.toLocaleString()} coins/月</p>
                    <p className={`text-xs whitespace-nowrap ${getPerformanceColor(candidate.performance)}`}>
                      能力値: {candidate.performance}
                    </p>
                  </div>
                  {isOwner && (
                    <button
                      onClick={() => handleHire(candidate)}
                      disabled={alreadyHired || company.employees.length >= company.maxEmployees}
                      className={`flex-shrink-0 text-xs px-4 py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        alreadyHired
                          ? isDark ? "bg-white/5 text-gray-600 cursor-not-allowed" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-emerald-400/15 text-emerald-400 hover:bg-emerald-400/25 border border-emerald-400/30"
                      }`}
                    >
                      {alreadyHired ? "採用済" : "採用する"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
